import { useEffect, useState, useCallback, useRef } from 'react';
import { orderPresenceService, PresenceState } from '../services/orderPresence.service';
import appConfig from '../config/app';

interface UseOrderPresenceOptions {
  orderId: number | null;
  userId: string;
  enabled?: boolean;
}

interface UseOrderPresenceReturn {
  presenceState: PresenceState;
  isConnected: boolean;
}

/**
 * Custom hook for managing order presence
 * 
 * Automatically:
 * - Connects to WebSocket when enabled
 * - Joins order when component mounts
 * - Leaves order when component unmounts
 * - Handles presence updates
 * 
 * @param options Configuration options
 * @returns Current presence state and connection status
 */
export const useOrderPresence = ({
  orderId,
  userId,
  enabled = true,
}: UseOrderPresenceOptions): UseOrderPresenceReturn => {
  const [presenceState, setPresenceState] = useState<PresenceState>({
    ownerUserId: null,
    ownerName: null,
    showBanner: false,
  });

  const [isConnected, setIsConnected] = useState(false);
  const previousOrderId = useRef<number | null>(null);

  const handlePresenceUpdate = useCallback((state: PresenceState) => {
    setPresenceState(state);
  }, []);

  useEffect(() => {
    if (!enabled || !userId) {
      return;
    }

    orderPresenceService.connect(appConfig?.newApi, userId)
    
    const initialConnectCheck = setTimeout(() => {
      setIsConnected(orderPresenceService.isConnected());
    }, 100);

    const connectionCheckInterval = setInterval(() => {
      setIsConnected(orderPresenceService.isConnected());
    }, 1000);

    return () => {
      clearTimeout(initialConnectCheck);
      clearInterval(connectionCheckInterval);
    };
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled || !orderId || !userId || !isConnected) {
      return;
    }

    let unsubscribe: (() => void) | null = null;

    if (previousOrderId.current && previousOrderId.current !== orderId) {
      orderPresenceService.leaveOrder(previousOrderId.current);
    }

    unsubscribe = orderPresenceService.onPresenceChange(orderId, handlePresenceUpdate);

    const connectTimeout = setTimeout(() => {
      orderPresenceService.joinOrder(orderId);
      previousOrderId.current = orderId;
    }, 100);

    return () => {
      clearTimeout(connectTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
      if (orderId && previousOrderId.current === orderId) {
        orderPresenceService.leaveOrder(orderId);
      }
    };
  }, [enabled, orderId, userId, handlePresenceUpdate, isConnected]);

  return {
    presenceState,
    isConnected,
  };
};
